import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'

interface User {
  id: string
  email: string
  name: string
  avatarUrl: string | null
  emailVerified: boolean
  role: string
  isAdmin?: boolean
  createdAt: string
  updatedAt: string
}

interface Session {
  user: User
  session: { id: string; userId: string; expiresAt: string; token: string }
}

export function useSession() {
  return useQuery({
    queryKey: ['session'],
    queryFn: () => api<Session>('/api/auth/get-session'),
    retry: false,
    staleTime: 1000 * 30, // 30 seconds — keep short so role changes propagate after billing events
  })
}

export function useSignIn() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { email: string; password: string }) =>
      api<Session>('/api/auth/sign-in/email', { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: (data) => {
      qc.setQueryData(['session'], data)
    },
  })
}

export function useSignUp() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { email: string; password: string; name: string }) =>
      api<Session>('/api/auth/sign-up/email', { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: (data) => {
      qc.setQueryData(['session'], data)
    },
  })
}

export function useSignOut() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api<void>('/api/auth/sign-out', { method: 'POST' }),
    onSuccess: () => {
      qc.clear()
    },
  })
}
